import {
  ADJUSTMENT_STATUS_VALUES,
  type AdjustmentStatus,
  type AdjustmentWarning,
  LIQUID_AC_PATTERN_VALUES,
  type LiquidAcPattern,
} from '@edgevanta/db/data-types';

/**
 * Liquid AC v2 legacy<->canonical mapping (ADR-0109 / EDG-1272).
 *
 * Single source of truth for translating the legacy v1 vocabulary to the
 * canonical v2 contract. The SQL backfill migration and the dbt mapping CTE are
 * authored to match this module exactly; parity tests assert they agree.
 *
 * This is the ONLY place new code should translate legacy pattern values — the
 * ADR forbids ad-hoc branching on legacy labels once the canonical path ships.
 */

/**
 * PostHog flag gating the whole Liquid AC adjustment feature (server-side).
 * Lives here (the vocab module) so both the forecast engine and the item
 * config service share one key without an import cycle.
 */
export const LIQUID_AC_FLAG = 'liquid-ac-adjustment';

/** Legacy DB pattern labels stored in `bid_item_liquid_ac.pattern` (v1). */
export type LegacyLiquidAcPattern =
  | '1'
  | '2'
  | '3-surface'
  | '3-liquid'
  | 'N/A';

/** Legacy JS aliases used by the reference impl (compute-adjusted-price.ts). */
export type LegacyLiquidAcAlias =
  | 'pattern_1_surface'
  | 'pattern_2_deep'
  | 'pattern_3_surface'
  | 'pattern_3_liquid'
  | 'n_a';

// A Map (not a plain object) so lookups by untrusted keys can never resolve a
// prototype member like `toString` / `__proto__`.
const LEGACY_ALIAS_TO_LABEL = new Map<string, LegacyLiquidAcPattern>([
  ['pattern_1_surface', '1'],
  ['pattern_2_deep', '2'],
  ['pattern_3_surface', '3-surface'],
  ['pattern_3_liquid', '3-liquid'],
  ['n_a', 'N/A'],
]);

const LEGACY_LABELS: ReadonlySet<string> = new Set([
  '1',
  '2',
  '3-surface',
  '3-liquid',
  'N/A',
]);

/** Legacy label -> canonical pattern. `3-surface`/`N/A` are non-adjustable (null). */
const LEGACY_LABEL_TO_CANONICAL: Record<
  LegacyLiquidAcPattern,
  LiquidAcPattern | null
> = {
  '1': 'P1_allin_TN',
  '2': 'P2_allin_SY',
  '3-liquid': 'P4_liquid',
  '3-surface': null,
  'N/A': null,
};

const LIQUID_AC_PATTERN_SET: ReadonlySet<string> = new Set(
  LIQUID_AC_PATTERN_VALUES,
);
const ADJUSTMENT_STATUS_SET: ReadonlySet<string> = new Set(
  ADJUSTMENT_STATUS_VALUES,
);

/**
 * Coerce a raw value to a valid `liquid_ac_pattern` enum member, or null.
 * Defensive against out-of-domain strings from the warehouse: an unexpected
 * value becomes null rather than failing the whole upsert batch on enum cast.
 */
export function coerceLiquidAcPattern(
  value: string | null | undefined,
): LiquidAcPattern | null {
  return value != null && LIQUID_AC_PATTERN_SET.has(value)
    ? (value as LiquidAcPattern)
    : null;
}

/** Coerce a raw value to a valid `adjustment_status` enum member, or null. */
export function coerceAdjustmentStatus(
  value: string | null | undefined,
): AdjustmentStatus | null {
  return value != null && ADJUSTMENT_STATUS_SET.has(value)
    ? (value as AdjustmentStatus)
    : null;
}

/** Legacy dbt `classification_source` values -> canonical ADR-0109 vocabulary. */
const LEGACY_CLASSIFICATION_SOURCE_TO_CANONICAL = new Map<string, string>([
  ['override', 'manual_override'],
  ['detected', 'item_list_inspection'],
]);

const CANONICAL_CLASSIFICATION_SOURCES: ReadonlySet<string> = new Set([
  'spec_book',
  'item_list_inspection',
  'manual_override',
  'parent_state_dot_inheritance',
]);

/**
 * Map the dbt classification source to the canonical vocabulary. Already-canonical
 * values pass through; legacy `detected` -> `item_list_inspection` and `override`
 * -> `manual_override`. `unclassified` and unknown values map to null (it is a
 * status, not a source).
 */
export function mapClassificationSource(
  value: string | null | undefined,
): string | null {
  if (value == null) {
    return null;
  }
  if (CANONICAL_CLASSIFICATION_SOURCES.has(value)) {
    return value;
  }
  return LEGACY_CLASSIFICATION_SOURCE_TO_CANONICAL.get(value) ?? null;
}

/** Normalize either legacy vocabulary (DB label or JS alias) to the DB label. */
export function normalizeLegacyPattern(
  value: string | null | undefined,
): LegacyLiquidAcPattern | null {
  if (value == null) {
    return null;
  }
  const aliased = LEGACY_ALIAS_TO_LABEL.get(value);
  if (aliased != null) {
    return aliased;
  }
  if (LEGACY_LABELS.has(value)) {
    return value as LegacyLiquidAcPattern;
  }
  return null;
}

/** Map a legacy pattern (label or alias) to the canonical v2 pattern, or null. */
export function legacyToCanonicalPattern(
  value: string | null | undefined,
): LiquidAcPattern | null {
  const label = normalizeLegacyPattern(value);
  return label == null ? null : LEGACY_LABEL_TO_CANONICAL[label];
}

/** Config flag: whether a legacy pattern denotes a liquid-AC-adjustable item. */
export function legacyNeedsLiquidAsphaltAdjustment(
  value: string | null | undefined,
): boolean {
  return legacyToCanonicalPattern(value) != null;
}

export interface LegacyDerivationInputs {
  /** Legacy DB pattern label (or JS alias). */
  pattern: string | null | undefined;
  shouldAdjust: boolean | null | undefined;
  historicalIndexAtBid: number | null | undefined;
  liquidAcPct: number | null | undefined;
  basis: number | null | undefined;
  /** Index-at-bid came from a proxy source (legacy `historical_index_is_proxy`). */
  historicalIndexIsProxy?: boolean | null;
  /** Resolved index grade differs from the item grade (`historical_index_grade_is_fallback`). */
  gradeFallbackUsed?: boolean | null;
}

export interface CanonicalDerivation {
  liquidAcPattern: LiquidAcPattern | null;
  needsLiquidAsphaltAdjustment: boolean;
  adjustmentStatus: AdjustmentStatus;
  adjustmentWarnings: AdjustmentWarning[];
}

/**
 * Derive the canonical runtime status from legacy v1 signals.
 *
 * Missing-input checks come BEFORE `applied` so a row missing a required input
 * (e.g. P1/P2 with NULL liquid_ac_pct) is never recorded as `applied`, which
 * would violate ADR-0109 invariant 3. `zero_actual_price` is intentionally not
 * derivable here because the warehouse fetch filters out zero/near-zero prices.
 *
 * This records a deterministic best-effort status for the historical bid row.
 * The authoritative runtime status is recomputed by the centralized engine
 * (EDG-1274) and re-synced from dbt going forward.
 */
export function deriveAdjustmentStatus(
  input: LegacyDerivationInputs,
): AdjustmentStatus {
  const label = normalizeLegacyPattern(input.pattern);
  if (input.pattern == null || label === 'N/A') {
    return 'unclassified';
  }
  if (label === '3-surface') {
    return 'not_adjustable';
  }
  // Non-null value that is not a recognized pattern (corrupt/legacy leak).
  if (label !== '1' && label !== '2' && label !== '3-liquid') {
    return 'unsupported_pattern';
  }
  // Adjustable patterns: '1', '2', '3-liquid'. Required-input checks first.
  if (input.historicalIndexAtBid == null) {
    return 'missing_index_at_bid';
  }
  if ((label === '1' || label === '2') && input.liquidAcPct == null) {
    return 'missing_liquid_ac_pct';
  }
  if (label === '3-liquid' && input.basis == null) {
    return 'missing_basis';
  }
  if (input.shouldAdjust === true) {
    return 'applied';
  }
  // Inputs present but v1 suppressed the adjustment (program gate off).
  return 'not_adjustable';
}

/**
 * Canonical runtime warnings, derived from the boolean provenance flags and
 * gated to adjustable patterns. Mirrors the dbt `adjustment_warnings` array.
 */
export function deriveAdjustmentWarnings(
  input: LegacyDerivationInputs,
): AdjustmentWarning[] {
  if (legacyToCanonicalPattern(input.pattern) == null) {
    return [];
  }
  const warnings: AdjustmentWarning[] = [];
  if (input.historicalIndexIsProxy === true) {
    warnings.push('proxy_index_used');
  }
  if (input.gradeFallbackUsed === true) {
    warnings.push('grade_fallback_used');
  }
  return warnings;
}

/**
 * Full deterministic legacy -> canonical projection for a bid-line row.
 * Mirrors the SQL backfill in migration `*_liquid_ac_v2_backfill` exactly.
 */
export function deriveCanonicalFromLegacy(
  input: LegacyDerivationInputs,
): CanonicalDerivation {
  return {
    liquidAcPattern: legacyToCanonicalPattern(input.pattern),
    needsLiquidAsphaltAdjustment: legacyNeedsLiquidAsphaltAdjustment(
      input.pattern,
    ),
    adjustmentStatus: deriveAdjustmentStatus(input),
    adjustmentWarnings: deriveAdjustmentWarnings(input),
  };
}
